// ============================================================
// CONTROLE DE ACESSO — perfis (admin / restrito / controlado) e módulos permitidos
// ============================================================
import { supabase } from './supabase';
import { NAV } from './navConfig';

// Perfis com restrição de tela e, opcionalmente, de horário.
const ROLES_RESTRITOS = ['restrito', 'controlado'];

// Carrega o perfil do usuário logado. Fail-closed: qualquer usuário
// autenticado sem linha em usuarios_perfis não recebe nenhum acesso.
export async function carregarPerfil(userId) {
  const { data: perfilRow } = await supabase
    .from('usuarios_perfis')
    .select('role')
    .eq('id', userId)
    .maybeSingle();

  const role = perfilRow?.role || null;

  if (role === 'admin') return { role, modulosPermitidos: null, horario: null };

  if (ROLES_RESTRITOS.includes(role)) {
    const [{ data: modulos }, { data: horarioRow }] = await Promise.all([
      supabase.from('perfil_modulos_permitidos').select('modulo_id').eq('role', role),
      supabase.from('perfil_restricao_horario').select('horario_inicio, horario_fim').eq('role', role).maybeSingle(),
    ]);
    return {
      role,
      modulosPermitidos: (modulos || []).map(m => m.modulo_id),
      horario: horarioRow ? { inicio: horarioRow.horario_inicio, fim: horarioRow.horario_fim } : null,
    };
  }

  return { role: null, modulosPermitidos: [], horario: null };
}

// Verifica se o horário atual está dentro da janela liberada para o perfil.
// perfil.horario=null significa sem restrição (admin ou perfil sem linha configurada).
export function dentroDoHorarioPermitido(perfil) {
  if (!perfil?.horario) return true;
  const agora = new Date();
  const minutoAtual = agora.getHours() * 60 + agora.getMinutes();
  const [hI, mI] = perfil.horario.inicio.split(':').map(Number);
  const [hF, mF] = perfil.horario.fim.split(':').map(Number);
  return minutoAtual >= (hI * 60 + mI) && minutoAtual <= (hF * 60 + mF);
}

function getGroupChildrenIds(moduloId) {
  const group = NAV.find(g => g.id === moduloId);
  return group?.children?.map(c => c.id) || [];
}

// Itens marcados adminOnly no NAV não são liberáveis pelo Controle de Acesso.
function ehAdminOnly(moduloId) {
  for (const group of NAV) {
    if (group.id === moduloId) return !!group.adminOnly;
    const child = group.children?.find(c => c.id === moduloId);
    if (child) return !!child.adminOnly;
  }
  return false;
}

// Verifica se um módulo pode ser acessado pelo perfil atual.
// - modulo=null (Menu Principal) sempre liberado
// - role='admin' sempre liberado
// - "menus" com children (ex: entradas_menu) liberam se ao menos 1 filho estiver liberado
export function moduloPermitido(moduloId, perfil) {
  if (moduloId === null) return true;
  if (!perfil) return false; // ainda carregando ou sem perfil cadastrado
  if (perfil.role === 'admin') return true;
  if (ehAdminOnly(moduloId)) return false;

  const permitidos = perfil.modulosPermitidos || [];
  if (permitidos.includes(moduloId)) return true;

  const filhos = getGroupChildrenIds(moduloId);
  if (filhos.length > 0) return filhos.some(childId => permitidos.includes(childId));

  return false;
}
